import { Outlet, useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Box, Divider, Icon, Text, Touchable } from '@factorialco/deprecated-design-system'
import { Delete, Upload } from '@factorialco/factorial-one-react/icons/app'
import { TrainingEmployeesDocument, TrainingMembershipsQuery } from 'generated/resources/graphql'
import { EmployeeClassesSidepanelCertificatesDocument } from 'generated/resources/graphql'
import { EmployeeClassesSidepanelDeleteCertificateDocument } from 'generated/resources/graphql'

import useDataTable from 'modules/core/components/DataTable/hooks/useDataTable'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FileItem, FilesList } from 'modules/trainings/components/Files/FilesList'
import { SessionsDataTable } from 'modules/trainings/components/Revamp/EmployeeClassesSidepanel/SessionsDatatable'
import { TrainingMembershipStatusTag } from 'modules/trainings/components/Shared/TrainingMembershipStatusTag'
import { useTrainingClasses } from 'modules/trainings/hooks/TrainingClasses/useTrainingClasses'

const EmployeesSidepanel = () => {
  const { data } = useDataTable<TrainingMembershipsQuery>()
  const [fetchTrainingEmployee, { data: employeeDetail }] = useLazyQuery(TrainingEmployeesDocument)

  const employee = employeeDetail?.employees.employeesConnection.nodes[0]
  const { id, employeeId } = useParams<{
    id: string
    employeeId: string
  }>()

  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const {
    trainingClasses,
    trainingStatus,
    trainingMembershipId,
    loading: loadingClasses,
  } = useTrainingClasses({
    trainingId: Number(id),
    accessId: Number(employeeId),
  })

  const readonly = !trainingClasses[0]?.training?._policies?.edit

  const handleEmployeeSelection = useUnsafeCallback(
    async (accessId: number) => {
      await fetchTrainingEmployee({ variables: { accessIds: [accessId] } })
    },
    [fetchTrainingEmployee]
  )

  useUnsafeEffect(() => {
    if (employeeId) {
      handleEmployeeSelection(Number(employeeId))
    }
  }, [employeeId, handleEmployeeSelection])

  const activeEmployeeIndex = useUnsafeMemo(
    () => data.findIndex((x) => x.accessId === employee?.access.id),
    [data, employee]
  )

  const previousEmployeeId = data[activeEmployeeIndex - 1]?.accessId
  const nextEmployeeId = data[activeEmployeeIndex + 1]?.accessId

  const navigateToEmployee = useUnsafeCallback(
    (newEmployeeId: number) => {
      navigate(
        navigation.trainings.list.details_revamp.view.participants.employees.fullPath({
          id: Number(id),
          employeeId: Number(newEmployeeId),
        })
      )
    },
    [navigate, id, navigation]
  )

  if (loadingClasses) return null

  return (
    <Sidepanel
      width="wideXL"
      open
      title={
        <Box flexDirection="row" justifyContent="center" alignItems="center" flexGrow>
          {previousEmployeeId && (
            <Touchable onClick={() => navigateToEmployee(previousEmployeeId)}>
              <Icon.Small icon="chevron-left" />
            </Touchable>
          )}

          <Box paddingX="s24">
            <Text.Normal weight="semibold">{employee?.fullName}</Text.Normal>
          </Box>

          {nextEmployeeId && (
            <Touchable onClick={() => navigateToEmployee(nextEmployeeId)}>
              <Icon.Small icon="chevron-right" />
            </Touchable>
          )}
        </Box>
      }
      onClose={() =>
        navigate(
          navigation.trainings.list.details_revamp.view.participants.fullPath({ id: Number(id) })
        )
      }
    >
      <Box gap="s16">
        <Box gap="s24">
          {trainingStatus && <TrainingMembershipStatusTag status={trainingStatus} />}
          <Text size="400" weight="semibold">
            {employee?.fullName}
          </Text>
          <Text size="300" weight="normal">
            {i18n.t('trainings.trainings_revamp.employee_sidepanel.subtitle')}
          </Text>
          <Certificates
            trainingId={Number(id)}
            employeeId={Number(employeeId)}
            trainingMembershipId={Number(trainingMembershipId)}
            readonly={readonly}
          />
        </Box>
        <Divider />
        <Box gap="s40">
          {trainingClasses.map((tr) => (
            <Box key={tr.id}>
              <SessionsDataTable
                accessId={Number(employeeId)}
                trainingClassId={Number(tr.id)}
                trainingClassName={tr.name ?? ''}
              />
            </Box>
          ))}
        </Box>
      </Box>
      <Outlet />
    </Sidepanel>
  )
}

function Certificates({
  trainingId,
  employeeId,
  trainingMembershipId,
  readonly,
}: {
  trainingId: number
  employeeId: number
  trainingMembershipId: number
  readonly: boolean
}) {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [deleteCertificate] = useMutation(EmployeeClassesSidepanelDeleteCertificateDocument, {
    extractorFn: (data) => data.trainingsMutations.deleteTrainingsTrainingMembershipFile,
    awaitRefetchQueries: true,
    refetchQueries: [EmployeeClassesSidepanelCertificatesDocument],
  })
  const source = useGraphqlDataSource({
    document: EmployeeClassesSidepanelCertificatesDocument,
    mapData: (data): FileItem[] =>
      data.trainings.trainingMembershipFilesConnection.nodes.map((item) => ({
        id: Number(item.id),
        filename: item.filename,
        title: item.filename,
        contentType: item.contentType,
        type: 'membership',
        size: item.size,
      })),
    mapVariables: () => ({
      trainingMembershipIds: [trainingMembershipId],
    }),
    mapPagination: (data) => ({
      total: Number(data.trainings.trainingMembershipFilesConnection.totalCount),
    }),
    primaryActions: () => ({
      onClick: () => {
        navigate(
          navigation.trainings.list.details_revamp.view.participants.employees.uploadCertificate.fullPath(
            {
              id: trainingId,
              employeeId: employeeId,
            }
          )
        )
      },
      label: i18n.t('common.upload'),
      icon: Upload,
    }),
    itemUrl: (item: FileItem) =>
      navigation.trainings.list.details_revamp.view.participants.employees.previewCertificate.fullPath(
        {
          id: trainingId,
          trainingMembershipId: trainingMembershipId,
          trainingMembershipFileId: String(item.id),
          employeeId: employeeId,
        }
      ),
    itemActions: (item: FileItem) => {
      if (readonly) return []
      return [
        {
          onClick: async () => {
            const result = await deleteCertificate({ variables: { id: item.id } })
            handleResult(result, () => {
              app.setFlash({
                type: 'wrong',
                message: i18n.t('trainings.training_files.delete_error'),
              })
            })
          },
          label: i18n.t('common.delete'),
          icon: Delete,
        },
      ]
    },
  })

  return (
    <Box>
      <Text.Normal weight="semibold" color="textPrimary" size="400">
        {i18n.t('trainings.trainings_revamp.training_detail.employees_table.certificates')}
      </Text.Normal>
      <FilesList
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.cerificates_employee_empty_state.title'
            ),
            description: i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.cerificates_employee_empty_state.description'
            ),
          },
        }}
      />
    </Box>
  )
}

export default EmployeesSidepanel
